// Leads as the campaign portal knows them: a lead code, tags and a masked mobile. Never a name, never a full
// mobile (SPEC §4). The tag groups here are the only list of tags; audiences filter on them.
import { z } from 'zod'
import { Channel, ChannelOrAll, IsoDate, IsoDateTime, LeadCode, MaskedMobile } from './common'
import { SuppressionReason } from './suppression'

const LEAD_SOURCES = ['partner-portal', 'apply-now', 'registry', 'bulk-uploader'] as const
export const LeadSource = z.enum(LEAD_SOURCES)
export type LeadSource = z.infer<typeof LeadSource>

/**
 * Every tag group with its values. A tag is written "group:value", e.g. consent:none. consent:ivr = the person
 * pressed the yes digit on an IVR call (C2). data:full = the registry holds the full person a lender call needs
 * (mobile, PAN, name, DOB, monthly income, employment, PIN code: R1); data:mobile-only = it does not, so the person
 * gets the Apply now link instead (plan v2 §5.1, the "full data?" step). city, emp and score are written only by a
 * classification workflow (C-F6): the result of reading the registry's fields, never the field itself.
 */
export const TAG_GROUPS = {
  consent: ['none', 'partner', 'firstfund', 'ivr'],
  contact: ['ok', 'dnd', 'no-whatsapp'], // WS1: no-whatsapp = Exotel said the number cannot receive WhatsApp
  place: ['serviceable', 'not-serviceable', 'unknown'],
  product: ['personal-loan', 'business-loan', 'gold-loan', 'bnpl'], // LT (2026-11-03): the loan type; a person may have several
  source: LEAD_SOURCES,
  partner: ['firstfund'], // always there; the real partners come from GET /partners (listPartners)
  data: ['full', 'mobile-only'],
  city: ['metro', 'non-metro'],
  emp: ['salaried', 'self-employed'],
  score: ['hot', 'normal'],
} as const
export type TagGroup = keyof typeof TAG_GROUPS
export const TagGroup = z.enum([
  'consent',
  'contact',
  'place',
  'product',
  'source',
  'partner',
  'data',
  'city',
  'emp',
  'score',
])

export const LeadRow = z.object({
  lead_code: LeadCode,
  mobile_masked: MaskedMobile,
  source: LeadSource,
  partner: z.string(),
  tags: z.array(z.string()),
  received: IsoDate,
  suppressed: z.object({ reason: SuppressionReason, channel: ChannelOrAll }).nullable(),
  last_message: z.object({ at: IsoDateTime, channel: Channel, outcome: z.string() }).nullable(),
})
export type LeadRow = z.infer<typeof LeadRow>

export const LeadFilter = z.object({
  q: z.string().optional().describe('part of a lead code'),
  source: LeadSource.optional(),
  tag: z.string().optional().describe('one tag, e.g. place:serviceable'),
  page: z.number().int().min(1).optional(),
  page_size: z.number().int().min(1).max(200).optional(),
})
export type LeadFilter = z.infer<typeof LeadFilter>

export const LeadPage = z.object({
  items: z.array(LeadRow),
  total: z.number().int().min(0),
  page: z.number().int().min(1),
  page_size: z.number().int().min(1),
})
export type LeadPage = z.infer<typeof LeadPage>

/** CW4: what happened to one campaign message, as the person's history keeps it. */
export const HistoryStatus = z.enum(['sent', 'delivered', 'read', 'failed', 'replied', 'stop', 'clicked'])
export type HistoryStatus = z.infer<typeof HistoryStatus>

/** A message's outcome here → its history status; null = nothing to show (queued, skipped …). */
export function historyStatusOf(outcome: string): HistoryStatus | null {
  if (outcome === 'replied STOP') return 'stop'
  return HistoryStatus.options.find((s) => s === outcome) ?? null
}

export const LeadHistoryInput = z.object({ lead_code: LeadCode })
export type LeadHistoryInput = z.infer<typeof LeadHistoryInput>

export const LeadHistoryItem = z.object({
  at: IsoDateTime,
  channel: Channel,
  status: HistoryStatus,
  campaign_id: z.string(),
  campaign_name: z.string().nullable().describe('null: a campaign this portal does not have (any more)'),
  template: z.string().nullable().describe('the template or voice message name (sent only), never its words'),
})
export type LeadHistoryItem = z.infer<typeof LeadHistoryItem>

/**
 * CW4: the person's campaign history (the History drawer on Leads), newest first, at most 200. From the lead registry's
 * footprints; when the registry does not answer (or does not know the person yet), from this portal's own messages.
 * Never a mobile, a name or the words of a reply.
 */
export const LeadHistory = z.object({
  lead_code: LeadCode,
  source: z.enum(['registry', 'portal']),
  marketing_stopped_at: IsoDateTime.nullable(),
  items: z.array(LeadHistoryItem),
})
export type LeadHistory = z.infer<typeof LeadHistory>

export interface LeadsApi {
  listLeads(filter: LeadFilter): Promise<LeadPage>
  /** CW4: one person's campaign history (POST: no lead code in a URL) */
  leadHistory(input: LeadHistoryInput): Promise<LeadHistory>
}
